import React from 'react';

import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import SidePanel from 'src/components/sidePanel/SidePanel';
import { badgeCardTypeFilterOptions, badgeCardTypeOptions } from 'src/features/cards/utils/constants';
import { cardMessages } from 'src/features/cards/utils/messages';
import { useListPoints } from 'src/features/points/api/useListPoints';
import { PointType } from 'src/features/points/types';
import { useListSpacesSimpleStatistics } from 'src/features/statistics/api/useListSpacesSimpleStatistics';
import { useListVisitsStatistics } from 'src/features/statistics/api/useListVisitsStatistics';
import { CustomDataTableControls } from 'src/features/statistics/components/CustomDataTableControls';
import ParkingVisitsExport from 'src/features/statistics/components/ParkingVisitsExport';
import ParkingVisitSidePanel from 'src/features/statistics/components/ParkingVisitSidePanel';
import StatisticsCards from 'src/features/statistics/components/StatisticsCards';
import { VISITS_EXPORT_PROPS } from 'src/features/statistics/types/visits';
import { statisticsMessages } from 'src/features/statistics/utils';
import { useListSubParkings } from 'src/features/subParkings/api/useListSubParkings';
import useFilters from 'src/hooks/useFilters';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import {
    SectionBlock,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSeparator,
    SectionBlockSubtitle,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellPrice } from 'src/registry/components/table/cells/CellPrice';
import DataTableDateDropdown from 'src/registry/components/table/DataTableDateDropdown';
import useRowSelection from 'src/registry/hooks/useRowSelection';
import { Button } from 'src/registry/ui/Button';
import { useTitleStore } from 'src/stores/useTitleStore';

import type { VisitsStatistic } from 'src/features/statistics/types';

const columnHelper = createColumnHelper<VisitsStatistic>();
const PARKING_VISITS_PANEL_NAME = 'parking-visits-panel';

export default function ParkingVisits() {
    const { parkingId } = useParking();

    const { setTitle, removeTitle } = useTitleStore();
    const [dateTime, setDateTime] = React.useState(() => {
        const currentDate = new Date();
        const today = new Date(currentDate.getTime());
        today.setHours(23);
        today.setMinutes(59);
        today.setSeconds(59);
        const lastWeek = new Date(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7).getTime());
        return { afterDate: lastWeek, beforeDate: today };
    });

    const { formatMessage } = useIntl();

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'enter_datetime', desc: false }],
    });

    const { data: subparkingsData } = useListSubParkings({
        parkingId,
    });
    const { data: pointData } = useListPoints({ parkingId });

    const { entryPoints, exitPoints } = React.useMemo(() => {
        const result: { entryPoints: string[]; exitPoints: string[] } = {
            entryPoints: [],
            exitPoints: [],
        };
        if (!pointData) return result;
        return pointData.results.reduce((acc, point) => {
            point.type === PointType.ENTRY ? acc.entryPoints.push(point.name) : acc.exitPoints.push(point.name);
            return acc;
        }, result);
    }, [pointData]);

    const { clearRowSelection, ...rowSelection } = useRowSelection({ sidePanelName: PARKING_VISITS_PANEL_NAME });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });

    const { data: simpleSpacesData } = useListSpacesSimpleStatistics({
        parkingId,
        from_date: dateTime.afterDate.toISOString(),
        to_date: dateTime.beforeDate.toISOString(),
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
    });

    const { data, isLoading, isError } = useListVisitsStatistics({
        parkingId,
        to_date: dateTime.beforeDate.toISOString(),
        from_date: dateTime.afterDate.toISOString(),
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
    });

    const DataTableControls = React.useMemo(
        () =>
            CustomDataTableControls(
                <>
                    <DataTableDateDropdown
                        setValue={setDateTime}
                        inline
                        afterDate={dateTime.afterDate}
                        beforeDate={dateTime.beforeDate}
                    />
                    <ParkingVisitsExport
                        disabled={Boolean(data?.count && data?.count < 1)}
                        parkingId={parkingId}
                        dateTime={dateTime}
                        querySortingOptions={querySortingOptions}
                        queryFiltersOptions={queryFiltersOptions}
                        querySearchOptions={querySearchOptions}
                    />
                </>,
            ),
        [setDateTime, dateTime, parkingId, querySortingOptions, queryFiltersOptions, querySearchOptions],
    );

    React.useEffect(() => {
        setTitle(formatMessage(statisticsMessages.visits));
        return removeTitle;
    }, []);

    const getRowData = React.useMemo(() => {
        const firstSelected = Object.keys(rowSelection.rowSelectionState)[0];
        if (!data || !firstSelected) return;
        return data.results.find((visits) => visits.id.toString() === firstSelected);
    }, [data, rowSelection.rowSelectionState]);

    const columns = [
        columnHelper.accessor(VISITS_EXPORT_PROPS.CARD_NUMBER, {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(cardMessages.cardNumber),
            },
        }),
        columnHelper.accessor(VISITS_EXPORT_PROPS.OWNER_NAME, {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(cardMessages.ownerName),
            },
        }),
        columnHelper.accessor(VISITS_EXPORT_PROPS.CARD_TYPE, {
            header: DataTableColumnHeader,
            cell: (props) => <CellBadge badgeOptions={badgeCardTypeOptions} {...props} />,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(cardMessages.cardType),
                badgeOptions: badgeCardTypeFilterOptions,
            },
        }),
        columnHelper.accessor(VISITS_EXPORT_PROPS.ENTER_DATETIME, {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(cardMessages.enterDatetime),
            },
        }),
        columnHelper.accessor(VISITS_EXPORT_PROPS.EXIT_DATETIME, {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(statisticsMessages.exitDatetime),
            },
        }),
        columnHelper.accessor(VISITS_EXPORT_PROPS.ENTRY_POINT, {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableColumnFilter: true,
            enableHiding: true,
            meta: {
                title: formatMessage(cardMessages.entryPoint),
                options: entryPoints.map((point) => ({
                    value: point,
                    label: point,
                })),
            },
        }),
        columnHelper.accessor(VISITS_EXPORT_PROPS.EXIT_POINT, {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(cardMessages.exitPoint),
                options: exitPoints.map((point) => ({
                    value: point,
                    label: point,
                })),
            },
        }),
        columnHelper.accessor(VISITS_EXPORT_PROPS.PARKING_NAME, {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(statisticsMessages.parkingZone),
                options: subparkingsData?.results.map((subparking) => ({
                    value: subparking.id,
                    label: subparking.name,
                })),
            },
            id: 'parking',
        }),
        columnHelper.accessor(VISITS_EXPORT_PROPS.CHARGE_PLAN_NAME, {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(cardMessages.chargePlan),
            },
        }),
        columnHelper.accessor(VISITS_EXPORT_PROPS.PRICE_PAID, {
            header: DataTableColumnHeader,
            cell: (ctx) => <CellPrice {...ctx} currency={ctx.row.original.currency} />,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(statisticsMessages.paid),
            },
        }),
        columnHelper.accessor(VISITS_EXPORT_PROPS.PRICE_PAID_TIME, {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(statisticsMessages.paidAt),
            },
        }),
    ];

    const cardData = React.useMemo(() => {
        const data = simpleSpacesData?.results[0];
        return [
            {
                title: formatMessage(statisticsMessages.totalNumberVisits),
                value: data?.total_count || 0,
            },
            {
                title: formatMessage(statisticsMessages.vipVisits),
                value: data?.vip_count || 0,
            },
            {
                title: formatMessage(statisticsMessages.prePaidVisits),
                value: data?.prepaid_count || 0,
            },
            {
                title: formatMessage(statisticsMessages.normalVisits),
                value: data?.normal_count || 0,
            },
        ];
    }, [simpleSpacesData]);

    return (
        <SectionBlock>
            <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                <Link to="/statistics/reports">
                    <FontAwesomeIcon icon={faChevronLeft} className="opacity-50" />
                    {formatMessage(statisticsMessages.backToAllReports)}
                </Link>
            </Button>
            <SectionBlockHeader>
                <SectionBlockHead>
                    <SectionBlockTitle>{formatMessage(statisticsMessages.allVisits)}</SectionBlockTitle>
                    <SectionBlockSubtitle>{formatMessage(statisticsMessages.allVisitsSubtitle)}</SectionBlockSubtitle>
                </SectionBlockHead>
            </SectionBlockHeader>
            <SectionBlockSeparator />
            <StatisticsCards cardData={cardData} />
            <>
                <DataTable
                    columns={columns}
                    data={data}
                    pagination={pagination}
                    sorting={sorting}
                    filters={filters}
                    search={search}
                    semantics={{
                        title: formatMessage(statisticsMessages.visits),
                        entryName: formatMessage(statisticsMessages.visit),
                        entryNames: formatMessage(statisticsMessages.visits),
                        placeholder: `${formatMessage(cardMessages.cardNumber)}, ${formatMessage(cardMessages.ownerName)}`,
                        sidePanelName: PARKING_VISITS_PANEL_NAME,
                    }}
                    isError={isError}
                    isLoading={isLoading}
                    rowSelection={rowSelection}
                    getRowId={(row) => row.id.toString()}
                    components={{
                        controls: DataTableControls,
                        pagination: DataTablePagination,
                    }}
                    enableLocalFilters={true}
                />
                <SidePanel onClose={clearRowSelection} name={PARKING_VISITS_PANEL_NAME}>
                    <ParkingVisitSidePanel visit={getRowData} />
                </SidePanel>
            </>
        </SectionBlock>
    );
}
